import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface AiToolSource {
  toolName: string;
  description: string;
  isActive: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface AiSourceListResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    tenantName: string;
    sources: AiToolSource[];
  };
}

export interface AiSourceToggleResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: AiToolSource;
}

export type CustomSourceHttpMethod = 'GET' | 'POST' | 'PUT';
export type CustomSourceAuthType = 'none' | 'apiKey' | 'bearer' | 'basic';
export type CustomSourceParamType = 'string' | 'number' | 'boolean';

export interface CustomSourceParameter {
  name: string;
  type: CustomSourceParamType;
  required: boolean;
  description: string;
}

export interface CustomSourceAuthConfig {
  headerName?: string;
  apiKey?: string;
  token?: string;
  username?: string;
  password?: string;
}

export interface CustomToolSource {
  toolName: string;
  description: string;
  endpointUrl: string;
  httpMethod: CustomSourceHttpMethod;
  authType: CustomSourceAuthType;
  parameters: CustomSourceParameter[];
  isActive: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
  createdBy: string | null;
}

export interface RegisterCustomSourceRequest {
  toolName: string;
  description: string;
  endpointUrl: string;
  httpMethod: CustomSourceHttpMethod;
  authType: CustomSourceAuthType;
  authConfig: CustomSourceAuthConfig | null;
  parameters: CustomSourceParameter[];
}

export interface CustomSourceListResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    tenantName: string;
    sources: CustomToolSource[];
  };
}

export interface CustomSourceResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: CustomToolSource;
}

export interface CustomSourceTestResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    statusCode: number;
    durationMs: number;
    body: unknown;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AiSources {
  private baseUrl = `${environment.apiUrl}/ai-sources`;

  constructor(private http: HttpClient) {}

  getAll(): Observable<AiSourceListResponse> {
    return this.http.get<AiSourceListResponse>(this.baseUrl);
  }

  toggle(toolName: string, isActive: boolean): Observable<AiSourceToggleResponse> {
    return this.http.patch<AiSourceToggleResponse>(`${this.baseUrl}/${toolName}/toggle`, { isActive });
  }

  getCustomSources(): Observable<CustomSourceListResponse> {
    return this.http.get<CustomSourceListResponse>(`${this.baseUrl}/custom`);
  }

  registerCustomSource(payload: RegisterCustomSourceRequest): Observable<CustomSourceResponse> {
    return this.http.post<CustomSourceResponse>(`${this.baseUrl}/custom`, payload);
  }

  updateCustomSource(toolName: string, payload: RegisterCustomSourceRequest): Observable<CustomSourceResponse> {
    return this.http.put<CustomSourceResponse>(`${this.baseUrl}/custom/${toolName}`, payload);
  }

  deleteCustomSource(toolName: string): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/custom/${toolName}`);
  }

  testCustomSource(payload: RegisterCustomSourceRequest, sampleArgs: Record<string, unknown>): Observable<CustomSourceTestResponse> {
    return this.http.post<CustomSourceTestResponse>(`${this.baseUrl}/custom/test`, { ...payload, sampleArgs });
  }
}
